import {Directive, inject} from '@angular/core';
import {PlayersService} from "../../services/players.service";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {IPlayerPaymentMethodQueryResult} from "../../interfaces/player/IPlayerPaymentMethodQueryResult";
import {FormControl, FormGroup} from "@angular/forms";
import {IPlayerPaymentMethodFilter} from "../../interfaces/player/IPlayerPaymentMethodFilter";
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {MatDialog} from "@angular/material/dialog";
import {Observable, of, switchMap} from "rxjs";
import {DataAccessService} from "../../services/data-access.service";
import {AppConfigService} from "../../services/app-config.service";
import {DataAccessReasonDialogComponent} from "../../components/dialogs/data-access-reason/data-access-reason.component";
import {IDataAccessAuthorization} from "../../interfaces/audit/IDataAccessAuthorization";

@Directive()
export abstract class PaymentMethodsBaseComponent {
  /** Must match MaxPaymentMethodResults on PlayerPaymentMethodAdminController. */
  protected static readonly MaxResults = 500;

  /** Must match PlayerPaymentMethodsQueryRequest.MinimumFuzzyFilterLength. */
  protected static readonly MinimumFuzzyFilterLength = 3;

  private playerService: PlayersService = inject(PlayersService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private dialog: MatDialog = inject(MatDialog);
  private dataAccessService: DataAccessService = inject(DataAccessService);
  private appConfigService: AppConfigService = inject(AppConfigService);

  protected uiState: UIStateEnum = UIStateEnum.ShowData;
  protected uiStateForTemplate = UIStateEnum;
  protected paymentMethods: IPlayerPaymentMethodQueryResult[] = [];
  protected paymentMethodsGridApi!: GridApi<IPlayerPaymentMethodQueryResult>;
  protected game: IGameQueryResult | null = null;
  protected isCrossGame = false;
  protected hasSearched = false;
  protected resultsTruncated = false;
  protected filterRequiredMessage = '';
  protected maxResultsForTemplate = PaymentMethodsBaseComponent.MaxResults;

  /**
   * The reason stated for this game, kept so every later search can be recorded against it without
   * re-showing the dialog. One audit row per search, not one per visit: an admin who states a reason
   * and then looks up twenty players must leave twenty records, or "who viewed player X's card
   * details, and why?" cannot be answered from the log (#1507 review).
   */
  private statedReason: { accessReason: number; additionalDetails: string } | null = null;

  protected filterForm = new FormGroup({
    playerId: new FormControl(''),
    paymentMethodId: new FormControl(''),
    cardFingerprint: new FormControl(''),
    cardLastFourDigits: new FormControl(''),
    playerEmail: new FormControl('')
  });

  protected columnDefs: ColDef[] = [
    {
      headerName: 'Player Email',
      field: 'PlayerEmail',
      resizable: true,
      sortable: true,
      filter: true,
      width: 250
    },
    {
      headerName: 'Payment Method ID',
      field: 'Id',
      resizable: true,
      sortable: true,
      width: 250
    },
    {
      headerName: 'Player ID',
      field: 'PlayerId',
      resizable: true,
      sortable: true,
      width: 250
    },
    {
      headerName: 'Last 4 Digits',
      field: 'CardLastFourDigits',
      resizable: true,
      sortable: true,
      width: 120
    },
    {
      headerName: 'Exp Month',
      field: 'CardExpirationMonth',
      resizable: true,
      sortable: true,
      width: 100
    },
    {
      headerName: 'Exp Year',
      field: 'CardExpirationYear',
      resizable: true,
      sortable: true,
      width: 100
    },
    {
      headerName: 'Card Fingerprint',
      field: 'CardFingerprint',
      resizable: true,
      sortable: true,
      width: 200
    },
    {
      headerName: 'Active',
      field: 'Active',
      resizable: true,
      sortable: true,
      width: 100,
      cellRenderer: (params: { value: boolean }) => params.value ? 'Yes' : 'No'
    },
    {
      headerName: 'Deactivated On',
      field: 'DeactivatedOn',
      resizable: true,
      sortable: true,
      width: 180,
      valueFormatter: (params: { value: string | null }) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleString();
      }
    }
  ];

  onGameChosen(chosenGameP: IGameQueryResult): void {
    this.game = chosenGameP;
    this.paymentMethods = [];
    this.hasSearched = false;
    this.resultsTruncated = false;
    this.filterRequiredMessage = '';

    // A stated reason authorizes access to ONE game's player data — the audit record carries that
    // game id. Switching games must re-prompt, or the second game's lookups are never recorded.
    this.statedReason = null;
  }

  onSearchClick(): void {
    this.searchPaymentMethods();
  }

  onResetClick(): void {
    this.filterForm.reset();
    this.paymentMethods = [];
    this.hasSearched = false;
    this.filterRequiredMessage = '';
  }

  /**
   * This lookup returns player emails and card details, so it is search-first: nothing is loaded
   * until the admin narrows to a card or a player, and the first search of a visit is gated behind
   * the data access reason dialog so the access is recorded against a stated reason.
   */
  protected searchPaymentMethods(): void {
    if (!this.game) {
      return;
    }

    if (!this.hasIdentifyingFilter()) {
      this.paymentMethods = [];
      this.hasSearched = false;
      this.filterRequiredMessage = `Enter a player ID, payment method ID or last 4 digits, or at least ${PaymentMethodsBaseComponent.MinimumFuzzyFilterLength} characters of a player email or card fingerprint, to search.`;
      return;
    }

    this.filterRequiredMessage = '';

    if (this.statedReason || this.appConfigService.privacyPromptsDisabled()) {
      this.runSearch();
      return;
    }

    this.authorizeThenSearch();
  }

  /**
   * Mirrors PlayerPaymentMethodsQueryRequest.HasIdentifyingFilter. Email and fingerprint are
   * substring matches server-side, so a one- or two-character value is not a search — it is the bulk
   * player list with a cap on it (#1507 review). The GUID filters and last 4 are exact.
   */
  protected hasIdentifyingFilter(): boolean {
    const value = this.filterForm.value;

    const exact_filters = [value.playerId, value.paymentMethodId, value.cardLastFourDigits];
    const fuzzy_filters = [value.cardFingerprint, value.playerEmail];

    return exact_filters.some(filterValue => !!filterValue && filterValue.trim() !== '')
      || fuzzy_filters.some(filterValue => !!filterValue && filterValue.trim().length >= PaymentMethodsBaseComponent.MinimumFuzzyFilterLength);
  }

  private authorizeThenSearch(): void {
    const dialogRef = this.dialog.open(DataAccessReasonDialogComponent, {
      data: { gameId: this.game!.Id },
      disableClose: true,
      width: '900px'
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (!result || !this.game) {
          return;
        }

        // The reason is held, not logged here: the audit row is written per search by runSearch, so
        // the first search and the twentieth are both recorded against it.
        this.statedReason = {
          accessReason: result.accessReason,
          additionalDetails: result.additionalDetails || ''
        };

        this.runSearch();
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  /**
   * One record per search, written BEFORE the rows are fetched. The stated reason is reused so the
   * admin sees no second dialog, but an admin who states a reason and then looks up twenty players
   * leaves twenty records — otherwise "who viewed player X's card details, and why?" cannot be
   * answered from the log (#1507 review).
   *
   * Order matters: the audit row is written first and the search only runs if that succeeded, so a
   * failed audit write blocks the disclosure rather than trailing it.
   */
  private logSearchAccess(): Observable<unknown> {
    if (!this.statedReason || !this.game) {
      return of(null);
    }

    const audit_data: IDataAccessAuthorization = {
      GameId: this.game.Id,
      AccessReason: this.statedReason.accessReason,
      AdditionalDetails: [
        this.statedReason.additionalDetails,
        `Payment method lookup filtered by ${this.describeFilters()}.`
      ].filter(part => !!part).join(' | '),
      PlayerName: `Payment Method Lookup - ${this.game.Name}`
    };

    // A lookup by player id is about a known player, so the row can name them.
    const player_id = this.filterForm.value.playerId?.trim();

    if (player_id) {
      audit_data.PlayerId = player_id;
    }

    return this.dataAccessService.logDataAccessAuthorization(audit_data);
  }

  /** The filters used, names only — never the values, which are themselves the PII being searched. */
  private describeFilters(): string {
    const value = this.filterForm.value;

    const used = [
      ['player id', value.playerId],
      ['payment method id', value.paymentMethodId],
      ['card fingerprint', value.cardFingerprint],
      ['last 4 digits', value.cardLastFourDigits],
      ['player email', value.playerEmail]
    ].filter(([, filterValue]) => !!filterValue && (filterValue as string).trim() !== '')
     .map(([name]) => name);

    return used.join(', ');
  }

  private runSearch(): void {
    if (!this.game) {
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    const filter: IPlayerPaymentMethodFilter = {
      gameId: this.game.Id,
      playerId: this.filterForm.value.playerId || undefined,
      paymentMethodId: this.filterForm.value.paymentMethodId || undefined,
      cardFingerprint: this.filterForm.value.cardFingerprint || undefined,
      cardLastFourDigits: this.filterForm.value.cardLastFourDigits || undefined,
      playerEmail: this.filterForm.value.playerEmail || undefined
    };

    // Audit first, disclose second — see logSearchAccess.
    this.logSearchAccess().pipe(
      switchMap(() => this.playerService.getPaymentMethodsByGame(filter))
    ).subscribe({
      next: (paymentMethods) => {
        this.paymentMethods = paymentMethods;
        this.hasSearched = true;
        this.resultsTruncated = paymentMethods.length >= PaymentMethodsBaseComponent.MaxResults;
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  onGridReady(params: GridReadyEvent<IPlayerPaymentMethodQueryResult>): void {
    this.paymentMethodsGridApi = params.api;
    this.paymentMethodsGridApi.sizeColumnsToFit({
      defaultMinWidth: 100
    });
  }
}
